import { useState } from 'react';
import { Card, CardHeader } from '@/shared/components/ui/Card';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '@/shared/components/ui/Table';
import { Badge } from '@/shared/components/ui/Badge';
import type { LocationDashboardSummary, AlertSeverity } from '@/shared/types/domain.types';

interface LocationSummaryTableProps {
  summaries: LocationDashboardSummary[];
  onLocationClick: (
    locationId: string,
    view: 'receiving' | 'weekly-ordering' | 'reports',
    section?: 'usage' | 'activity'
  ) => void;
}

/**
 * Get badge variant for severity
 */
function getBadgeVariant(severity: AlertSeverity): 'success' | 'warning' | 'danger' | 'neutral' {
  switch (severity) {
    case 'red':
      return 'danger';
    case 'yellow':
      return 'warning';
    case 'green':
      return 'success';
    default:
      return 'neutral';
  }
}

/**
 * Get status indicator (colored dot)
 */
function StatusIndicator({ severity }: { severity: AlertSeverity }) {
  const color =
    severity === 'red'
      ? 'bg-red-500'
      : severity === 'yellow'
      ? 'bg-yellow-500'
      : severity === 'green'
      ? 'bg-green-500'
      : 'bg-gray-400';

  return <span className={`inline-block w-3 h-3 rounded-full ${color}`} />;
}

type SortField = 'location' | 'usage' | 'overdue' | 'pending';
type SortDirection = 'asc' | 'desc';

export function LocationSummaryTable({ summaries, onLocationClick }: LocationSummaryTableProps) {
  const [sortField, setSortField] = useState<SortField>('location');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const sortedSummaries = [...summaries].sort((a, b) => {
    let comparison = 0;

    switch (sortField) {
      case 'location':
        comparison = a.location_name.localeCompare(b.location_name);
        break;
      case 'usage':
        comparison = a.usage_percentage - b.usage_percentage;
        break;
      case 'overdue':
        comparison = a.overdue_ordered_count - b.overdue_ordered_count;
        break;
      case 'pending':
        comparison = a.stale_pending_count - b.stale_pending_count;
        break;
    }

    return sortDirection === 'asc' ? comparison : -comparison;
  });

  return (
    <Card padding="none">
      <CardHeader>
        <h2 className="text-lg font-semibold">Location Summary</h2>
        <p className="text-sm text-gray-600 mt-1">Click any metric to view details for that location</p>
      </CardHeader>
      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead sortable onSort={() => handleSort('location')}>
                Location
              </TableHead>
              <TableHead sortable onSort={() => handleSort('usage')} className="text-center">
                Usage %
              </TableHead>
              <TableHead sortable onSort={() => handleSort('overdue')} className="text-center">
                <span className="hidden md:inline">Overdue Ordered</span>
                <span className="md:hidden">Overdue</span>
              </TableHead>
              <TableHead sortable onSort={() => handleSort('pending')} className="text-center">
                <span className="hidden md:inline">Stale Pending</span>
                <span className="md:hidden">Stale</span>
              </TableHead>
              <TableHead className="text-center">
                <span className="hidden md:inline">Receiving Backlog</span>
                <span className="md:hidden">Backlog</span>
              </TableHead>
              <TableHead className="text-center">
                <span className="hidden md:inline">Ordering Day</span>
                <span className="md:hidden">Order Day</span>
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {sortedSummaries.map((summary) => (
              <TableRow key={summary.location_id}>
                {/* Location Name */}
                <TableCell>
                  <span className="font-medium text-gray-900 hidden lg:inline">{summary.location_name}</span>
                  <span className="font-medium text-gray-900 lg:hidden">{summary.location_short_code || summary.location_name}</span>
                </TableCell>

                {/* Usage % */}
                <TableCell className="text-center">
                  <button
                    onClick={() => onLocationClick(summary.location_id, 'reports', 'usage')}
                    className="inline-flex items-center space-x-2 hover:bg-gray-50 px-2 py-1 rounded transition-colors"
                  >
                    <StatusIndicator severity={summary.usage_severity} />
                    <span className="font-semibold">{summary.usage_percentage}%</span>
                  </button>
                </TableCell>

                {/* Overdue Ordered */}
                <TableCell className="text-center">
                  {summary.overdue_ordered_count > 0 ? (
                    <button
                      onClick={() => onLocationClick(summary.location_id, 'receiving')}
                      className="inline-flex items-center space-x-2 hover:bg-gray-50 px-2 py-1 rounded transition-colors"
                    >
                      <StatusIndicator severity={summary.overdue_ordered_severity} />
                      <Badge variant={getBadgeVariant(summary.overdue_ordered_severity)} size="sm">
                        {summary.overdue_ordered_count}
                      </Badge>
                    </button>
                  ) : (
                    <span className="text-gray-400">—</span>
                  )}
                </TableCell>

                {/* Stale Pending */}
                <TableCell className="text-center">
                  {summary.stale_pending_count > 0 ? (
                    <button
                      onClick={() => onLocationClick(summary.location_id, 'weekly-ordering')}
                      className="inline-flex items-center space-x-2 hover:bg-gray-50 px-2 py-1 rounded transition-colors"
                    >
                      <StatusIndicator severity={summary.stale_pending_severity} />
                      <Badge variant={getBadgeVariant(summary.stale_pending_severity)} size="sm">
                        {summary.stale_pending_count}
                      </Badge>
                    </button>
                  ) : (
                    <span className="text-gray-400">—</span>
                  )}
                </TableCell>

                {/* Receiving Backlog */}
                <TableCell className="text-center">
                  {summary.receiving_backlog_count > 0 ? (
                    <button
                      onClick={() => onLocationClick(summary.location_id, 'receiving')}
                      className="hover:bg-gray-50 px-2 py-1 rounded transition-colors"
                    >
                      <Badge variant="warning" size="sm">
                        {summary.receiving_backlog_count}
                      </Badge>
                    </button>
                  ) : (
                    <span className="text-gray-400">—</span>
                  )}
                </TableCell>

                {/* Ordering Day */}
                <TableCell className="text-center">
                  {summary.is_ordering_day ? (
                    <button
                      onClick={() => onLocationClick(summary.location_id, 'weekly-ordering')}
                      className="hover:bg-gray-50 px-2 py-1 rounded transition-colors"
                    >
                      <Badge variant="info" size="sm">
                        Today
                      </Badge>
                    </button>
                  ) : (
                    <span className="text-gray-400">—</span>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </Card>
  );
}
